"use client";
import { Modal } from "@/ui/index";
import { Button } from "@cmp-src/primitives/button";
import { doLocalUpload } from "@/features/settings/components/upload/utils";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { api } from "@/lib/api/api";
import { IResource } from "@gettactic/api";
import { useAuthenticated } from "@gettactic/hooks";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

type Props = {
  floor: IResource;
  setMapChanges: React.Dispatch<React.SetStateAction<number>>;
  mapChanges: number;
  svg: SVGSVGElement;
  isEditMap?: boolean;
};
export function UploadMapChanges({
  mapChanges,
  floor,
  svg,
  setMapChanges,
  isEditMap = false
}: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [uploading, setUploading] = useState(false);
  const [discardChangesModal, setDiscardChangesModal] = useState(false);
  const [uploadChangesModal, setUploadChangesModal] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async ({
      isEditMap,
      request
    }: {
      isEditMap: boolean;
      request: any;
    }) => {
      const res = await api.client.organizations.resourcePut(floor.id, request);
      return { res };
    },

    onSuccess: (result, variables) => {
      if (variables.isEditMap) {
        toast.success(
          <span>
            Map was updated successfully
            <br />
            <a
              className="underline"
              href="#"
              onClick={() => {
                location.reload();
              }}
            >
              Reload map
            </a>
          </span>,
          {
            duration: Number.POSITIVE_INFINITY
          }
        );
      } else {
        toast.success("Map was updated successfully");
      }
    },

    onError: (result) => {
      console.error(result);
      toast.error("Map could not be uploaded");
    }
  });

  useEffect(() => {
    if (isEditMap && mapChanges > 0) toast.dismiss();
  }, [mapChanges]);

  if (!mapChanges) {
    return null;
  }

  const uploadMap = async () => {
    setUploading(true);
    try {
      const s = new XMLSerializer();
      const svgData = s.serializeToString(svg);
      const file = new File([svgData], "new-map.svg", {
        type: "image/svg+xml"
      });
      const data = await doLocalUpload(file, "/api/uploads/map", {
        organizationId: authenticatedUser.organization?.id ?? "",
        resourceId: floor.id,
        officeId: floor.office_id
      });
      const resultUrl = `https://cdn.gettactic.com/${data.key}`;
      const request = {
        type: "area",
        parent_id: floor.parent_id,
        name: floor.name,
        total_slots: floor.total_slots,
        maximum_slots_per_user: floor.maximum_slots_per_user,
        is_open: floor.is_open,
        is_approvable: false,
        teams: null,
        users: null,
        map_url: resultUrl
      };
      uploadMutation.mutate({ isEditMap, request });
      setUploading(false);
      setMapChanges(0);
    } catch (e) {
      console.error(e);
      toast.error("Sorry we couldn't complete the upload");
      setUploading(false);
      return;
    }
  };

  return (
    <>
      <div className="mr-8 flex flex-row items-center">
        <p className="rounded-md bg-neutral-900 p-2 text-xs text-white">
          {uploading ? (
            <>
              <span className="mr-1">
                New map is being uploaded. Please wait...
              </span>{" "}
              <LoadingSpinner />
            </>
          ) : null}
          {!uploading ? (
            <>
              Map changes detected.{" "}
              <a
                className="mx-1 font-bold underline"
                onClick={() => setUploadChangesModal(true)}
                href={"#"}
              >
                Upload changes {!isEditMap ? `(${mapChanges})` : null}
              </a>{" "}
              or
              <a
                onClick={() => setDiscardChangesModal(true)}
                className="mx-1 font-bold underline"
                href={"#"}
              >
                discard them.
              </a>
            </>
          ) : null}
        </p>
      </div>
      <Modal
        title="Upload New Map Version"
        isOpen={uploadChangesModal}
        onClose={() => setUploadChangesModal(false)}
      >
        <div>
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-red-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div className="mt-2">
                <p className="text-base text-neutral-800">
                  Are you sure you want to upload a new map version for{" "}
                  {floor.name}? Changes will be published to all users.
                </p>
              </div>
            </div>
          </div>
        </div>
        <Modal.Footer>
          <Button
            className="w-full sm:w-auto"
            variant="outline"
            onClick={() => setUploadChangesModal(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              uploadMap();
              setUploadChangesModal(false);
            }}
            className="w-full sm:ml-3 sm:w-auto"
            type="submit"
          >
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        title="Discard Changes"
        isOpen={discardChangesModal}
        onClose={() => setDiscardChangesModal(false)}
      >
        <div>
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-red-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div className="mt-2">
                <p className="text-base text-neutral-800">
                  Are you sure you want to discard all changes? This action
                  cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>
        <Modal.Footer>
          <Button
            className="w-full sm:w-auto"
            variant="outline"
            onClick={() => setDiscardChangesModal(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // @todo
              // We do this temporally to discard changes but the right thing here is to
              // save a reference to old svg DOM.
              location.reload();
            }}
            className="w-full sm:ml-3 sm:w-auto"
            type="submit"
          >
            Discard
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
